import { useEffect, useRef, useState } from "react";
import { AlertCircle, MoreHorizontal, Plus } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import {
  CHANNEL_NAMES,
  connectionLabel,
  errorMessage,
  linkLimit,
  useConnections,
  useDisconnect,
  useTokenScopes,
  type Connection,
  type MessagingChannel,
} from "./api";
import { ChannelLogo } from "./ChannelLogo";
import { LinkChannelDialog, type LinkingState } from "./LinkChannelDialog";
import { LinkedPill } from "./LinkedPill";
import { McpTokensRow } from "./McpTokensRow";
import { FLAT_ON_PHONE, TAP_ON_PHONE } from "./classes";
import { Spinner } from "./Spinner";

/** The user's messaging numbers and MCP access tokens, on /profile. */
export function ConnectionsCard() {
  const connections = useConnections();
  const scopes = useTokenScopes();
  const channels = connections.data?.channels ?? [];
  // Rows that have arrived don't wait on the token API.
  const loading = connections.isLoading || (channels.length === 0 && scopes.isLoading);
  const failed = connections.isError || scopes.isError;

  // Nothing to link and no token API: the card says nothing.
  if (!loading && !failed && channels.length === 0 && !scopes.data) return null;

  const retry = () => {
    if (connections.isError) void connections.refetch();
    if (scopes.isError) void scopes.refetch();
  };

  return (
    <Card className={cn(FLAT_ON_PHONE, "max-sm:shadow-none")}>
      <CardHeader>
        <CardTitle role="heading" aria-level={2}>
          Connections
        </CardTitle>
      </CardHeader>
      <CardContent className="divide-y p-0">
        {failed && (
          <div className="px-5 pb-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="flex items-center justify-between gap-2">
                Connections didn't load.
                <Button variant="ghost" size="sm" className={TAP_ON_PHONE} onClick={retry}>
                  Try again
                </Button>
              </AlertDescription>
            </Alert>
          </div>
        )}
        {loading && (
          <div className="flex justify-center px-5 pb-4">
            <Spinner label="Loading connections" />
          </div>
        )}
        {channels.map((channel) => (
          <ChannelRows
            key={channel}
            channel={channel}
            connections={connections.data?.connections.filter((c) => c.channel === channel) ?? []}
            limit={linkLimit(connections.data, channel)}
          />
        ))}
        <McpTokensRow />
      </CardContent>
    </Card>
  );
}

/**
 * A channel's links, one row each. A channel that takes several (WhatsApp) adds numbers under
 * them up to its limit; one that takes a single link (Telegram) switches its number instead.
 */
function ChannelRows({
  channel,
  connections,
  limit,
}: {
  channel: MessagingChannel;
  connections: Connection[];
  limit: number;
}) {
  // The links that already exist are fixed when the dialog opens: it is done once another shows up.
  // It stays set after the dialog closes, so the title and body don't change while it animates out.
  const [linking, setLinking] = useState<LinkingState | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const name = CHANNEL_NAMES[channel];
  const known = connections.map((connection) => connection.id);
  const several = limit > 1;
  // The disconnect lives here, not in its row: the row goes with it, and focus moves to the
  // channel's Link or Add button.
  const disconnect = useDisconnect();
  const linkButton = useRef<HTMLButtonElement>(null);
  // One disconnect at a time per channel: a second one in flight replaces the first's state.
  const { isSuccess: disconnected, variables: disconnectedId, reset: resetDisconnect } = disconnect;
  // The success can render before the refreshed list does: focus waits until the row has gone.
  // If the refresh fails the row stays, and focus stays on its options.
  const gone = disconnected && !known.includes(disconnectedId ?? -1);
  useEffect(() => {
    if (!gone) return;
    // The options went with the row and took focus with them. If the list came late (a failed
    // refresh, then a refetch) and the user has moved on, focus stays where they put it.
    const focus = document.activeElement;
    if (!focus || focus === document.body) linkButton.current?.focus();
    // Only this disconnect moves focus: a later unlink (a poll, another tab) leaves it alone.
    resetDisconnect();
  }, [gone, resetDisconnect]);
  // Rows without a number can share a label ("WhatsApp account"): the later ones are numbered.
  const seen = new Map<string, number>();
  const labels = connections.map((connection) => {
    const label = connectionLabel(connection);
    const count = (seen.get(label) ?? 0) + 1;
    seen.set(label, count);
    return count > 1 ? `${label} ${count}` : label;
  });
  // Each row's options button, by link id.
  const optionsButtons = useRef(new Map<number, HTMLButtonElement>());
  const startLinking = (state: LinkingState) => {
    setLinking(state);
    setDialogOpen(true);
  };
  const focusOnClose = (event: Event) => {
    // The dialog has no trigger, so Radix would drop focus on the body. A new link goes to its
    // row's options; otherwise focus goes back to Link or Add, or to the first row's options (the
    // one a Switch opened from: only a channel that takes one link switches).
    event.preventDefault();
    const openedWith = linking?.known ?? [];
    const added = connections.find((connection) => !openedWith.includes(connection.id));
    const target =
      (added && optionsButtons.current.get(added.id)) ||
      (linkButton.current?.isConnected ? linkButton.current : undefined) ||
      (connections[0] && optionsButtons.current.get(connections[0].id));
    target?.focus();
  };

  return (
    <>
      {connections.length === 0 ? (
        <div className="flex min-w-0 items-center gap-3 px-5 py-4">
          <ChannelLogo channel={channel} linked={false} />
          <span className="font-medium">{name}</span>
          <Button
            ref={linkButton}
            className={cn("ml-auto", TAP_ON_PHONE)}
            onClick={() => startLinking({ known })}
          >
            Link {name}
          </Button>
        </div>
      ) : (
        connections.map((connection, index) => (
          <ConnectionRow
            key={connection.id}
            connection={connection}
            label={labels[index]}
            optionsRef={(button) => {
              if (button) optionsButtons.current.set(connection.id, button);
              else optionsButtons.current.delete(connection.id);
            }}
            onSwitch={several ? undefined : () => startLinking({ known, replacing: connection.id })}
            onDisconnect={() => disconnect.mutate(connection.id)}
            error={
              disconnect.isError && disconnectedId === connection.id
                ? errorMessage(disconnect.error)
                : undefined
            }
          />
        ))
      )}
      {/* At the limit the Add button just goes: the /link page says why when it matters. */}
      {several && connections.length > 0 && connections.length < limit && (
        <div className="flex min-w-0 items-center gap-3 px-5 py-4">
          <Button
            ref={linkButton}
            variant="outline"
            className={TAP_ON_PHONE}
            onClick={() => startLinking({ known, adding: true })}
          >
            <Plus className="h-4 w-4" />
            Add another number
          </Button>
        </div>
      )}
      <LinkChannelDialog
        channel={channel}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        linking={linking ?? { known }}
        onCloseAutoFocus={focusOnClose}
      />
    </>
  );
}

function ConnectionRow({
  connection,
  label,
  optionsRef,
  onSwitch,
  onDisconnect,
  error,
}: {
  connection: Connection;
  /** The row's label, numbered when another row of the channel has the same one. */
  label: string;
  optionsRef: (button: HTMLButtonElement | null) => void;
  onSwitch?: () => void;
  onDisconnect: () => void;
  error?: string;
}) {
  const [confirming, setConfirming] = useState(false);
  const options = useRef<HTMLButtonElement | null>(null);
  // The fallback label ("WhatsApp account") already names the channel: it isn't said twice.
  const generic = !connection.phone && !connection.display_name;
  const rowName = generic ? label : `${CHANNEL_NAMES[connection.channel]} ${label}`;

  return (
    <div className="flex flex-col gap-2 px-5 py-4">
      <div className="flex min-w-0 items-center gap-3">
        <ChannelLogo channel={connection.channel} linked />
        {/* On a phone the name has a line of its own, with the number and status under it, so every
            row in a list has the same shape whatever its digits. From sm up they share one line,
            and the number wraps under the name with the status beside it if they can't fit. */}
        <div className="flex min-w-0 flex-col items-start gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-3">
          <span className="font-medium">{generic ? label : CHANNEL_NAMES[connection.channel]}</span>
          <span className="flex min-w-0 max-w-full items-center gap-2 sm:gap-3">
            {!generic && <span className="min-w-0 break-words text-sm tabular-nums">{label}</span>}
            <LinkedPill />
          </span>
        </div>
        <span className="ml-auto flex shrink-0 items-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                ref={(button) => {
                  options.current = button;
                  optionsRef(button);
                }}
                variant="ghost"
                size="icon"
                className="max-sm:h-10 max-sm:w-10"
                aria-label={`${rowName} options`}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {onSwitch && (
                <DropdownMenuItem className="max-sm:min-h-11" onSelect={onSwitch}>
                  Switch number
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                className="text-destructive max-sm:min-h-11"
                onSelect={() => setConfirming(true)}
              >
                Disconnect
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </span>
      </div>
      {error && (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      )}
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent
          aria-describedby={undefined}
          onCloseAutoFocus={(event) => {
            // Opened from the menu, not a trigger: focus goes back to the row's options.
            event.preventDefault();
            options.current?.focus();
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect {rowName}?</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={TAP_ON_PHONE}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={cn(
                "bg-destructive text-destructive-foreground hover:bg-destructive/90",
                TAP_ON_PHONE
              )}
              onClick={onDisconnect}
            >
              Disconnect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
